import { LoadingState } from '@lcs/shared/api';
import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import {
   createRegisterAdapter,
   RegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import {
   accountCallDetailsAdded,
   accountCallDetailsUpdated,
} from 'src/app/signalr/call-details-hub/call-details-hub.actions';
import { ActiveCallsRegisterOptions } from '../../models/active-calls-register-options';
import { ActiveCallsRegisterListItem } from '../../models/active-calls-register-results.model';
import * as actions from '../account-active-calls/account-active-calls.actions';
import {
   convertActionToActiveRegisterListItem,
   createCallRegisterReducer,
   matchesProviderFilter,
} from '../register-reducer.factory';
import { SharedCallRegisterState } from '../shared-register-state.model';

export const accountActiveCallsFeatureKey = 'accountActiveCalls';

export interface AccountActiveCallsState extends SharedCallRegisterState {}

const adapter: RegisterAdapter<ActiveCallsRegisterListItem, ActiveCallsRegisterOptions, OrderingOption> =
   createRegisterAdapter<ActiveCallsRegisterListItem, ActiveCallsRegisterOptions, OrderingOption>({
      selectId: (item) => item.callDetailId ?? 0,
   });

const entityAdapter: EntityAdapter<ActiveCallsRegisterListItem> = createEntityAdapter<ActiveCallsRegisterListItem>({
   selectId: (item) => item.callDetailId,
   sortComparer: (a, b) => b.callDetailId - a.callDetailId,
});

const initialState: AccountActiveCallsState = {
   ...adapter.getInitialState(),
   filters: [],
   isAutoRefresh: true,
   orderingOptions: [{ direction: OrderingDirections.Desc, field: 'startTime' }],
   callState: LoadingState.Init,
   quickFilters: null,
   endCursor: null,
   hasNextPage: false,
   totalCount: 0,
};

export const accountActiveCallsReducer = createCallRegisterReducer(
   adapter,
   entityAdapter,
   actions.accountActiveCallsRegisterAdapterActions,
   initialState,
   on(actions.accountActiveCallsRegisterInitialFilter, (state, action) => {
      return {
         ...state,
         filters: action.filters,
      };
   }),
   on(actions.setOrderingOptionsFromSessionStorage, (state, action) => ({
      ...state,
      orderingOptions: action.orderingOptions,
   })),
   on(accountCallDetailsAdded, (state, action) => {
      var accountId;
      for (var filter of state.filters) {
         if (filter.field == 'AccountId') {
            accountId = filter.value;
            break;
         }
      }
      var isCallOnThisAccount = action.callDetail.account.accountId === accountId;
      var searchFilter = [
         action.callDetail.account.name,
         action.callDetail.sourceExtension?.name,
         action.callDetail.destinationExtension?.name,
         action.callDetail.destinationNumber,
         action.callDetail.sourceNumber,
         action.callDetail.destinationExtension?.number.toString(),
         action.callDetail.destinationExtension?.extensionId.toString(),
         action.callDetail.sourceExtension?.number.toString(),
         action.callDetail.sourceExtension?.extensionId.toString(),
         action.callDetail.serverName,
      ];
      var matchesFilters = true;
      if (state.quickFilters) {
         matchesFilters = false;
         var matchesAccountFilter = false;
         var matchesProviderIdFilter = matchesProviderFilter(action.callDetail, state.quickFilters);
         var matchesSearchFilter = false;
         if (!state.quickFilters.searchText) {
            matchesSearchFilter = true;
         }
         if (!state.quickFilters.account) {
            matchesAccountFilter = true;
         }
         if (!matchesSearchFilter) {
            var searchText = '';
            if (state.quickFilters.searchText) {
               searchText = state.quickFilters.searchText.toLowerCase();
            }
            for (var currentFilter of searchFilter) {
               if (currentFilter?.toLowerCase().includes(searchText)) {
                  matchesSearchFilter = true;
                  break;
               }
            }
         }
         if (!matchesAccountFilter && action.callDetail.accountId === state.quickFilters.account?.accountId) {
            matchesAccountFilter = true;
         }
         matchesFilters = matchesAccountFilter && matchesProviderIdFilter && matchesSearchFilter;
      }
      if (accountId && isCallOnThisAccount && matchesFilters) {
         if (action.callDetail.endTime) {
            return state;
         }
         const callDetail = convertActionToActiveRegisterListItem(action);
         const newState = entityAdapter.addOne(callDetail, state);
         return {
            ...newState,
            totalCount: newState.ids.length,
         };
      }
      return state;
   }),

   on(accountCallDetailsUpdated, (state, action) => {
      const callDetail = convertActionToActiveRegisterListItem(action);
      if (!action.callDetail.endTime) {
         return entityAdapter.updateOne({ id: callDetail.callDetailId, changes: callDetail }, state);
      } else {
         const newState = entityAdapter.removeOne(callDetail.callDetailId, state);
         return {
            ...newState,
            totalCount: newState.ids.length,
         };
      }
   })
);

export const accountActiveCallsRegisterSelector =
   createFeatureSelector<AccountActiveCallsState>(accountActiveCallsFeatureKey);
export const selectors = adapter.getSelectors(accountActiveCallsRegisterSelector);
export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectEndCursor,
   selectQuickFilters,
   selectOrderingOptions,
} = adapter.getSelectors(accountActiveCallsRegisterSelector);
